import { useState, useRef, useCallback, useMemo } from 'react'
import type { Datapoint } from './types'

interface MetricChartProps {
  datapoints: Datapoint[] | null | undefined
  color?: string
  height?: number
  showTooltip?: boolean
  label?: string
  unit?: string
}

function monotonePath(points: [number, number][]): string {
  if (points.length < 2) return ''
  const n = points.length
  const d: string[] = [`M${points[0][0]},${points[0][1]}`]

  for (let i = 0; i < n - 1; i++) {
    const cx = (points[i][0] + points[i + 1][0]) / 2
    d.push(`C${cx},${points[i][1]} ${cx},${points[i + 1][1]} ${points[i + 1][0]},${points[i + 1][1]}`)
  }
  return d.join(' ')
}

function formatValue(value: number, unit?: string): string {
  if (unit === '%') return `${value.toFixed(1)}%`
  if (unit === 'ms') return `${value.toFixed(0)}ms`
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`
  return `${Math.round(value)}`
}

export function MetricChart({ datapoints, color = 'hsl(var(--primary))', height = 60, showTooltip = true, label, unit }: MetricChartProps) {
  const svgRef = useRef<SVGSVGElement>(null)
  const [tooltip, setTooltip] = useState<{ x: number; y: number; datapoint: Datapoint } | null>(null)
  const gradientId = useMemo(() => `grad-${color.replace(/[^a-zA-Z0-9]/g, '')}`, [color])

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!showTooltip || !datapoints || datapoints.length === 0 || !svgRef.current) return
    const rect = svgRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const w = rect.width
    const idx = Math.round((x / w) * (datapoints.length - 1))
    const clamped = Math.max(0, Math.min(datapoints.length - 1, idx))
    setTooltip({ x: (clamped / (datapoints.length - 1)) * w, y: e.clientY - rect.top, datapoint: datapoints[clamped] })
  }, [showTooltip, datapoints])

  const handleMouseLeave = useCallback(() => setTooltip(null), [])

  if (!datapoints || datapoints.length === 0) {
    return (
      <div className="flex items-center justify-center border border-dashed border-muted-foreground/20 rounded" style={{ height }}>
        <span className="text-[11px] text-muted-foreground/50">No data</span>
      </div>
    )
  }

  const padding = 2
  const maxVal = Math.max(...datapoints.map(d => d.value))
  const minVal = Math.min(...datapoints.map(d => d.value))
  const range = maxVal - minVal || 1

  return (
    <div className="relative w-full" style={{ height }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 100 ${height}`}
        preserveAspectRatio="none"
        className="w-full h-full"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.2} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        {(() => {
          const points: [number, number][] = datapoints.map((d, i) => [
            (i / (datapoints.length - 1)) * 100,
            padding + ((maxVal - d.value) / range) * (height - padding * 2),
          ])
          const linePath = monotonePath(points)
          const areaPath = `${linePath} L${points[points.length - 1][0]},${height} L${points[0][0]},${height} Z`
          return (
            <>
              <path d={areaPath} fill={`url(#${gradientId})`} />
              <path d={linePath} fill="none" stroke={color} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
            </>
          )
        })()}
      </svg>
      {showTooltip && tooltip && (
        <div
          className="absolute pointer-events-none z-10 px-2 py-1 rounded-md border text-xs bg-popover text-popover-foreground shadow-md"
          style={{
            left: tooltip.x,
            top: 0,
            transform: 'translate(-50%, -110%)',
          }}
        >
          <div className="text-muted-foreground">
            {/^\d{4}-\d{2}-\d{2}$/.test(tooltip.datapoint.timestamp)
              ? new Date(tooltip.datapoint.timestamp + 'T00:00:00').toLocaleDateString([], { month: 'short', day: 'numeric' })
              : new Date(tooltip.datapoint.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
          <div style={{ color }}>
            {formatValue(tooltip.datapoint.value, unit)}{label ? ` ${label}` : ''}
          </div>
        </div>
      )}
    </div>
  )
}

export function SparklineChart({ datapoints, color = 'hsl(var(--primary))' }: { datapoints: Datapoint[] | null | undefined; color?: string }) {
  return <MetricChart datapoints={datapoints} color={color} height={40} showTooltip={false} />
}
